import React, { useEffect, useState } from 'react';
import { Table, Button, Select, Modal, Form, Input, DatePicker, App, Space, Typography } from 'antd';
import { PlusOutlined, CustomerServiceOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import WelcomeBanner from '../../Components/common/WelcomeBanner';
import { ApiErrorState, EmptyState } from '../../Components/common/StateViews';
import StatusTag from '../../Components/common/StatusTag';
import useApiData from '../../Hooks/useApiData';
import ticketService from '../../Services/ticketService';
import cioService from '../../Services/cioService';
import { selectAuth } from '../../store/authSlice';
import { TicketStatusLabels, TicketStatusColors, optionsFromLabels, ROLES } from '../../Constants/enums';
import { formatDateTime } from '../../Utils/format';
import { toApiError } from '../../api/axiosClient';

const STATUS_OPTIONS = [{ value: '', label: 'All statuses' }, ...optionsFromLabels(TicketStatusLabels)];

const Support = () => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const { roles } = useSelector(selectAuth);
  // CIO/PlatformAdmin manage the queue (create/assign/customer filter); Support
  // agents only see their own assigned tickets and can change status.
  const canManage = (roles || []).includes(ROLES.CIO) || (roles || []).includes(ROLES.PLATFORM_ADMIN);
  const [clientId, setClientId] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const { data, loading, error, reload } = useApiData(
    () => ticketService.getTickets({ clientId, status, page, pageSize }),
    [clientId, status, page, pageSize]
  );
  const [assignees, setAssignees] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    // Assignee + customer lookups are CIO/PlatformAdmin only; Support agents
    // don't have access to /clients or the assignee directory.
    if (!canManage) return;
    ticketService.getAssignees().then(setAssignees).catch(() => setAssignees([]));
    cioService.getClients({ pageSize: 100 }).then((r) => setCustomers(r?.items || [])).catch(() => setCustomers([]));
  }, [canManage]);

  const assigneeOptions = assignees.map((a) => ({
    value: a.id,
    label: `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.email,
  }));

  const onCreate = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return; // invalid — field errors render inline
    }
    setCreating(true);
    try {
      await ticketService.createTicket({
        clientId: values.clientId,
        issue: values.issue,
        occurredAtUtc: values.occurred?.toISOString(),
      });
      message.success('Ticket created.');
      setCreateOpen(false);
      form.resetFields();
      reload();
    } catch (err) {
      message.error(toApiError(err).message);
    } finally {
      setCreating(false);
    }
  };

  const changeStatus = async (r, next) => {
    setBusyId(r.id);
    try {
      await ticketService.setStatus(r.id, next);
      message.success('Ticket status updated.');
      reload();
    } catch (err) {
      message.error(toApiError(err).message);
    } finally {
      setBusyId(null);
    }
  };

  const reassign = async (r, userId) => {
    setBusyId(r.id);
    try {
      await ticketService.assign(r.id, userId);
      message.success('Ticket reassigned.');
      reload();
    } catch (err) {
      message.error(toApiError(err).message);
    } finally {
      setBusyId(null);
    }
  };

  const columns = [
    { title: 'Ticket', dataIndex: 'ticketNumber', render: (v, r) => <Typography.Link onClick={() => navigate(`/cio/support/${r.id}`)}>{v}</Typography.Link> },
    { title: 'Client', dataIndex: 'clientName' },
    {
      title: 'Issue',
      dataIndex: 'issue',
      render: (v) => (
        <div
          title={v}
          style={{ maxWidth: 420, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {v}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (v, r) => (
        <Space>
          <StatusTag value={v} labels={TicketStatusLabels} colors={TicketStatusColors} />
          <Select size="small" value={v} loading={busyId === r.id} style={{ width: 130 }} onChange={(next) => changeStatus(r, next)} options={optionsFromLabels(TicketStatusLabels)} />
        </Space>
      ),
    },
    ...(canManage
      ? [
          {
            title: 'Assigned to',
            dataIndex: 'assignedToUserId',
            render: (v, r) => (
              <Select
                size="small"
                value={v ?? undefined}
                placeholder="Unassigned"
                style={{ width: 160 }}
                loading={busyId === r.id}
                options={assigneeOptions}
                onChange={(userId) => reassign(r, userId)}
              />
            ),
          },
        ]
      : []),
    { title: 'Occurred', dataIndex: 'occurredAtUtc', render: formatDateTime },
  ];

  return (
    <div>
      <WelcomeBanner
        title="Tech Support & Issues"
        subtitle="Clients raise tickets, the team resolves them, the client follows the status"
        icon={<CustomerServiceOutlined />}
        extra={
          canManage ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
              New Ticket
            </Button>
          ) : null
        }
      />

      <Space style={{ marginBottom: 16 }} wrap>
        {canManage && (
          <Select
            showSearch
            optionFilterProp="label"
            value={clientId}
            onChange={(v) => { setPage(1); setClientId(v); }}
            style={{ width: 240 }}
            options={[
              { value: '', label: 'All customers' },
              ...customers.map((c) => ({ value: c.id, label: `${c.clientName} (${c.accountNumber})` })),
            ]}
          />
        )}
        <Select value={status} onChange={(v) => { setPage(1); setStatus(v); }} options={STATUS_OPTIONS} style={{ width: 200 }} />
      </Space>

      {error ? (
        <ApiErrorState error={error} onRetry={reload} />
      ) : !loading && (data?.items || []).length === 0 ? (
        <EmptyState description="No tickets for this filter." />
      ) : (
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={data?.items || []}
          scroll={{ x: 'max-content' }}
          pagination={{
            current: data?.page || page,
            pageSize: data?.pageSize || pageSize,
            total: data?.totalCount || 0,
            showSizeChanger: true,
            onChange: (p, s) => { setPage(p); setPageSize(s); },
          }}
        />
      )}

      <Modal
        title="New Support Ticket"
        open={createOpen}
        onOk={onCreate}
        onCancel={() => setCreateOpen(false)}
        confirmLoading={creating}
        okText="Create ticket"
        destroyOnHidden
      >
        <Form form={form} layout="vertical" requiredMark="optional">
          <Form.Item name="clientId" label="Client" rules={[{ required: true }]}>
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Select a client"
              options={customers.map((c) => ({ value: c.id, label: `${c.clientName} (${c.accountNumber})` }))}
            />
          </Form.Item>
          <Form.Item name="occurred" label="Time occurred" rules={[{ required: true }]}>
            <DatePicker showTime style={{ width: '100%' }} format="YYYY-MM-DD HH:mm" />
          </Form.Item>
          <Form.Item name="issue" label="Issue" rules={[{ required: true }]}>
            <Input.TextArea rows={3} placeholder="Short description of the problem" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Support;
